import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Reveal from "@/components/Reveal";
import { staffStamp, staffRedeem, staffSetStamps, fetchMembers, deleteMember, fetchSeasonalNote, saveSeasonalNote, fetchReviews, deleteReview } from "@/lib/api";
import { KeyRound, Stamp, Gift, IceCreamCone, Coffee, Users, Search, Trash2, Plus, Megaphone, Star } from "lucide-react";
import { toast } from "sonner";

const MembersPanel = ({ pin }) => {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [confirmId, setConfirmId] = useState(null);

  const load = () => fetchMembers(pin).then(setData).catch(() => toast.error("Could not load members"));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const stampMember = async (m) => {
    try {
      await staffStamp(pin, { code: m.email });
      toast.success(`Stamp added for ${m.name}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add stamp");
    }
  };

  const redeemMember = async (m) => {
    try {
      await staffRedeem(pin, { code: m.email, reward: "free treat" });
      toast.success(`Free treat redeemed for ${m.name}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Card is not full yet");
    }
  };

  const removeMember = async (m) => {
    try {
      await deleteMember(pin, m.id);
      toast.success(`${m.name} removed`);
      setConfirmId(null);
      load();
    } catch {
      toast.error("Could not remove member");
    }
  };

  if (!data) return <p className="text-center font-display italic text-teal/50 text-xl py-10">Loading the circle...</p>;

  const members = data.members.filter((m) =>
    (m.name + " " + m.email).toLowerCase().includes(query.toLowerCase()));

  const STATS = [
    [data.stats.members, "Members"],
    [data.stats.joined_this_month, "Joined this month"],
    [data.stats.stamps_out, "Stamps out"],
    [data.stats.freebies_given, "Freebies given"],
    [data.stats.birthdays_this_month, "Birthdays this month"],
  ];

  return (
    <div data-testid="members-panel">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
        {STATS.map(([n, label]) => (
          <div key={label} className="bg-white rounded-2xl border border-gold/25 p-4 text-center shadow-md shadow-teal/5">
            <p className="font-display font-black text-3xl text-teal" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>{n}</p>
            <p className="text-[10px] uppercase tracking-[0.15em] text-teal/60 mt-1">{label}</p>
          </div>
        ))}
      </div>

      <div className="relative mb-6">
        <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-teal/40" />
        <input placeholder="Search by name or email" data-testid="members-search" value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full bg-transparent border-b border-teal/30 focus:border-gold focus:outline-none py-2 pl-7 text-teal placeholder:text-teal/40 transition-colors" />
      </div>

      {members.length === 0 ? (
        <p className="text-center font-display italic text-teal/50 text-xl py-10">No members found.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {members.map((m) => (
            <li key={m.id} data-testid={`member-row-${m.id}`} className="bg-white rounded-2xl border border-gold/20 p-4 shadow-sm shadow-teal/5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="font-display font-bold text-teal truncate">{m.name}</p>
                  <p className="text-xs text-teal/60 truncate">{m.email}</p>
                  <p className="text-[11px] text-teal/50 mt-1">
                    {m.stamps || 0}/9 stamps · {m.freebies || 0} freebies
                    {m.birthday ? ` · birthday ${m.birthday.day}/${m.birthday.month}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => stampMember(m)} data-testid={`member-stamp-${m.id}`} aria-label={`Add stamp for ${m.name}`}
                    className="w-9 h-9 rounded-full bg-teal text-cream grid place-items-center hover:bg-gold hover:text-teal-deep transition-colors">
                    <Plus size={15} />
                  </button>
                  {(m.stamps || 0) >= 9 && (
                    <button onClick={() => redeemMember(m)} data-testid={`member-redeem-${m.id}`}
                      className="rounded-full bg-gold text-teal-deep text-[10px] uppercase tracking-[0.1em] font-semibold px-3 py-2 hover:bg-teal hover:text-cream transition-colors">
                      Redeem
                    </button>
                  )}
                  {confirmId === m.id ? (
                    <button onClick={() => removeMember(m)} data-testid={`member-delete-confirm-${m.id}`}
                      className="rounded-full bg-red-500 text-white text-[10px] uppercase tracking-[0.1em] font-semibold px-3 py-2">
                      Sure?
                    </button>
                  ) : (
                    <button onClick={() => setConfirmId(m.id)} data-testid={`member-delete-${m.id}`} aria-label={`Remove ${m.name}`}
                      className="w-9 h-9 rounded-full border border-teal/25 grid place-items-center text-teal/60 hover:border-red-400 hover:text-red-500 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
const NotePanel = ({ pin }) => {
  const [form, setForm] = useState({ active: false, title: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchSeasonalNote()
      .then((n) => setForm({ active: !!n.active, title: n.title || "", message: n.message || "" }))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    if (busy) return;
    if (!form.title.trim() || !form.message.trim()) { toast.error("Add a title and message"); return; }
    setBusy(true);
    try {
      await saveSeasonalNote(pin, { active: form.active, title: form.title.trim(), message: form.message.trim() });
      toast.success(form.active ? "Note is live on the homepage" : "Note saved (hidden)");
    } catch (err) {
      if (err.response?.status === 403) { sessionStorage.removeItem("morellis_staff_pin"); }
      toast.error("Could not save — check your PIN");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-[2rem] border border-gold/25 shadow-xl shadow-teal/10 p-8" data-testid="note-panel">
      <p className="text-sm text-teal/70 mb-5">Shows a gold-trimmed announcement band at the top of the homepage. Switch off when the moment passes.</p>

      <div className="flex items-center justify-between mb-6">
        <span className="text-xs uppercase tracking-[0.25em] text-teal/60">Visibility</span>
        <button type="button" onClick={() => setForm({ ...form, active: !form.active })} data-testid="note-active-toggle"
          aria-pressed={form.active}
          className={`rounded-full px-5 py-2 text-xs uppercase tracking-[0.15em] font-semibold transition-colors duration-300 ${form.active ? "bg-teal text-cream" : "border border-teal/30 text-teal/70"}`}>
          {form.active ? "Live on site" : "Hidden"}
        </button>
      </div>

      <label className="text-xs uppercase tracking-[0.25em] text-teal/60" htmlFor="note-title">Title</label>
      <input id="note-title" data-testid="note-title-input" value={form.title} maxLength={80}
        onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Christmas at Morelli's"
        className="w-full bg-transparent border-b border-teal/30 focus:border-gold focus:outline-none py-2 text-teal placeholder:text-teal/40 transition-colors mt-1 mb-5" />

      <label className="text-xs uppercase tracking-[0.25em] text-teal/60" htmlFor="note-message">Message</label>
      <textarea id="note-message" data-testid="note-message-input" value={form.message} maxLength={240} rows={3}
        onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Mulled-wine affogatos are back, plus gift boxes for the tree — pop in and say hello."
        className="w-full bg-transparent border-b border-teal/30 focus:border-gold focus:outline-none py-2 text-teal placeholder:text-teal/40 transition-colors mt-1 resize-none" />
      <p className="text-xs text-teal/50 text-right mt-1">{form.message.length}/240</p>

      <button onClick={save} disabled={busy || !loaded} data-testid="note-save"
        className="mt-4 w-full rounded-full bg-teal text-cream font-semibold uppercase tracking-[0.2em] text-sm py-4 hover:bg-gold hover:text-teal-deep transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
        <Megaphone size={15} /> {busy ? "Saving..." : "Save Note"}
      </button>
    </div>
  );
};

const ReviewsPanel = ({ pin }) => {
  const [reviews, setReviews] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = () => fetchReviews().then((r) => setReviews(r.reviews)).catch(() => toast.error("Could not load reviews"));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await deleteReview(pin, id);
      toast.success("Review removed");
      setConfirmId(null);
      load();
    } catch {
      toast.error("Could not remove — check your PIN");
    }
  };

  if (!reviews) return <p className="text-center text-teal/50 text-sm py-10">Loading reviews...</p>;

  return (
    <div className="space-y-3" data-testid="reviews-panel">
      {reviews.length === 0 && <p className="text-center text-teal/50 text-sm py-10">No reviews yet.</p>}
      {reviews.map((r) => (
        <div key={r.id} className="bg-white rounded-2xl border border-gold/25 shadow-sm p-5 flex items-start gap-4" data-testid={`review-row-${r.id}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-display font-bold text-teal">{r.name}</p>
              <span className="flex text-gold" aria-label={`${r.rating} stars`}>
                {Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={12} fill="currentColor" />)}
              </span>
            </div>
            <p className="text-sm text-teal/70 mt-1 leading-relaxed">{r.text}</p>
          </div>
          {confirmId === r.id ? (
            <button onClick={() => remove(r.id)} data-testid={`review-confirm-delete-${r.id}`}
              className="shrink-0 rounded-full bg-red-600 text-white text-xs uppercase tracking-wider px-4 py-2 hover:bg-red-700 transition-colors">
              Sure?
            </button>
          ) : (
            <button onClick={() => setConfirmId(r.id)} data-testid={`review-delete-${r.id}`} aria-label={`Delete review by ${r.name}`}
              className="shrink-0 w-9 h-9 rounded-full border border-teal/20 grid place-items-center text-teal/50 hover:border-red-400 hover:text-red-500 transition-colors">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

// The NETUM scanner types the whole QR link (…/staff?code=XXXX) like a keyboard — pull the code out.
const extractCode = (raw) => {
  const m = String(raw || "").match(/[?&]code=([A-Za-z0-9-]+)/);
  return m ? m[1] : String(raw || "").trim();
};

const Staff = () => {
  const [params] = useSearchParams();
  const [pin, setPin] = useState(sessionStorage.getItem("morellis_staff_pin") || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("stamp");
  const [code, setCode] = useState(params.get("code") || "");
  const [member, setMember] = useState(null);
  const [setCount, setSetCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const codeRef = useRef(null);

  const enter = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await staffSetStamps(pin, { code: "__pin_check__", stamps: 0 }).catch((err) => {
        if (err.response?.status === 403) throw new Error("bad pin");
      });
      sessionStorage.setItem("morellis_staff_pin", pin);
      setAuthed(true);
    } catch {
      toast.error("Incorrect staff PIN");
    } finally {
      setBusy(false);
    }
  };

  const act = async (fn, payload, label) => {
    const effective = extractCode(payload.code || code);
    if (busy || !effective) { if (!effective) toast.error("Scan a member's QR code or enter their code"); return; }
    setBusy(true);
    try {
      const res = await fn(pin, { ...payload, code: effective });
      setMember({ ...res, code: effective });
      toast.success(label);
      if (fn === staffStamp) {
        setCode("");
        codeRef.current?.focus();
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 403) { setAuthed(false); sessionStorage.removeItem("morellis_staff_pin"); }
      toast.error(typeof d === "string" ? d : "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream pt-32 pb-24" data-testid="staff-page">
      <div className={`mx-auto px-6 ${tab === "members" || tab === "reviews" ? "max-w-3xl" : "max-w-md"}`}>
        <Reveal className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.4em] text-teal/70 font-medium">Staff Only</p>
          <h1 className="font-display font-bold text-4xl mt-3 text-teal">Stamp Station</h1>
          <div className="mt-5 h-px w-20 bg-gold mx-auto" />
        </Reveal>

        {!authed ? (
          <Reveal>
            <form onSubmit={enter} data-testid="staff-pin-form" className="bg-white rounded-[2rem] border border-gold/25 shadow-xl shadow-teal/10 p-8">
              <label className="text-xs uppercase tracking-[0.25em] text-teal/60" htmlFor="staff-pin">Staff PIN</label>
              <input id="staff-pin" required inputMode="numeric" maxLength={12} data-testid="staff-pin-input" value={pin}
                onChange={(e) => setPin(e.target.value)} type="password"
                className="w-full bg-transparent border-b border-teal/30 focus:border-gold focus:outline-none py-2 text-2xl text-teal tracking-[0.3em] transition-colors" />
              <button type="submit" disabled={busy} data-testid="staff-pin-submit"
                className="mt-6 w-full rounded-full bg-teal text-cream font-semibold uppercase tracking-[0.2em] text-sm py-4 hover:bg-gold hover:text-teal-deep transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {busy ? "Checking..." : <>Open Station <KeyRound size={15} /></>}
              </button>
            </form>
          </Reveal>
        ) : (
          <Reveal>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-8 max-w-xl mx-auto" role="tablist">
              {[["stamp", "Stamp"], ["members", "Members"], ["note", "Note"], ["reviews", "Reviews"]].map(([v, label]) => (
                <button key={v} role="tab" aria-selected={tab === v} data-testid={`staff-tab-${v}`} onClick={() => setTab(v)}
                  className={`rounded-full py-2.5 text-xs uppercase tracking-[0.15em] font-medium transition-colors duration-300 flex items-center justify-center gap-2 ${tab === v ? "bg-teal text-cream" : "border border-teal/30 text-teal/70 hover:border-gold"}`}>
                  {v === "members" ? <Users size={14} /> : v === "note" ? <Megaphone size={14} /> : v === "reviews" ? <Star size={14} /> : <Stamp size={14} />} {label}
                </button>
              ))}
            </div>

            {tab === "members" ? <MembersPanel pin={pin} /> : tab === "note" ? <NotePanel pin={pin} /> : tab === "reviews" ? <ReviewsPanel pin={pin} /> : (
              <div className="bg-white rounded-[2rem] border border-gold/25 shadow-xl shadow-teal/10 p-8">
                <form onSubmit={(e) => { e.preventDefault(); act(staffStamp, { code }, member?.card_full ? "Card full!" : "Stamp added"); }} data-testid="stamp-scan-form">
                  <label className="text-xs uppercase tracking-[0.25em] text-teal/60" htmlFor="member-code">Scan QR code or enter member code</label>
                  <input id="member-code" required autoFocus ref={codeRef} data-testid="member-code-input" value={code}
                    onChange={(e) => setCode(e.target.value)} placeholder="Point the scanner at their card"
                    className="w-full bg-transparent border-b border-teal/30 focus:border-gold focus:outline-none py-2 text-teal placeholder:text-teal/40 transition-colors mt-1" />
                  <p className="text-[11px] text-teal/50 mt-2">Scanner tip: a scan fills this in — the scanner&rsquo;s Enter adds the stamp.</p>

                  <button type="submit" disabled={busy}
                    data-testid="staff-add-stamp"
                    className="mt-5 w-full rounded-full bg-teal text-cream font-semibold uppercase tracking-[0.2em] text-sm py-4 hover:bg-gold hover:text-teal-deep transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                    <Stamp size={16} /> Add Stamp
                  </button>
                </form>

                {member && (
                  <div data-testid="staff-member-result" className="mt-6 bg-mint/50 rounded-2xl p-5 text-center">
                    <p className="font-display font-bold text-xl text-teal">{member.name}</p>
                    <p className="text-sm text-teal/70 mt-1">{member.stamps} / 9 stamps{member.freebies != null && ` · ${member.freebies} freebies earned`}</p>
                    {member.card_full && <p className="mt-2 font-display italic text-gold text-lg">Card full — redeem below!</p>}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 mt-6">
                  <button onClick={() => act(staffRedeem, { code: member?.code || code, reward: "free coffee" }, "Free coffee redeemed")} disabled={busy}
                    data-testid="staff-redeem-coffee"
                    className="rounded-full border-2 border-teal/50 text-teal text-xs uppercase tracking-[0.15em] py-3 hover:border-gold hover:text-gold transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5">
                    <Coffee size={14} /> Free Coffee
                  </button>
                  <button onClick={() => act(staffRedeem, { code: member?.code || code, reward: "free scoop" }, "Free scoop redeemed")} disabled={busy}
                    data-testid="staff-redeem-scoop"
                    className="rounded-full border-2 border-teal/50 text-teal text-xs uppercase tracking-[0.15em] py-3 hover:border-gold hover:text-gold transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5">
                    <IceCreamCone size={14} /> Free Scoop
                  </button>
                </div>

                <div className="mt-8 pt-6 border-t border-gold/20">
                  <p className="text-xs uppercase tracking-[0.25em] text-teal/60 mb-3">Paper card transfer</p>
                  <div className="flex items-center gap-2">
                    <input type="number" min="0" max="9" data-testid="staff-set-count" value={setCount}
                      onChange={(e) => setSetCount(Number(e.target.value))}
                      className="w-20 bg-transparent border-b border-teal/30 focus:border-gold focus:outline-none py-1.5 text-teal text-center transition-colors" />
                    <button onClick={() => act(staffSetStamps, { code: member?.code || code, stamps: setCount }, "Stamps transferred")} disabled={busy}
                      data-testid="staff-set-stamps"
                      className="flex-1 rounded-full bg-gold text-teal-deep text-xs uppercase tracking-[0.15em] font-semibold py-3 hover:bg-teal hover:text-cream transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5">
                      <Gift size={14} /> Set Stamps
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Reveal>
        )}
      </div>
    </div>
  );
};

export default Staff;
